import { Card, CardContent } from "@/components/ui/card";
import { Megaphone, CheckCircle2, Clock, XCircle, Calendar, Target, Building2, Eye } from "lucide-react";
import { Banner } from "@/services/bannerService";
import { cn } from "@/lib/utils";

interface BannerStatsProps {
  banners: Banner[];
}

type BannerStatus = 'active' | 'scheduled' | 'expired' | 'inactive';

const getBannerStatus = (banner: Banner): BannerStatus => {
  const now = new Date();
  const startDate = new Date(banner.startDate);
  const endDate = new Date(banner.endDate);

  if (!banner.active) return 'inactive';
  if (now < startDate) return 'scheduled';
  if (now > endDate) return 'expired';
  return 'active';
};

export function BannerStats({ banners }: BannerStatsProps) {
  const stats = {
    total: banners.length,
    active: banners.filter((b) => getBannerStatus(b) === 'active').length,
    scheduled: banners.filter((b) => getBannerStatus(b) === 'scheduled').length,
    expired: banners.filter((b) => getBannerStatus(b) === 'expired').length,
    inactive: banners.filter((b) => getBannerStatus(b) === 'inactive').length,
    withButton: banners.filter((b) => b.buttonText && b.buttonLink).length,
    targeted: banners.filter((b) => b.targetCompanies && b.targetCompanies.length > 0).length,
    global: banners.filter((b) => !b.targetCompanies || b.targetCompanies.length === 0).length,
  };

  const items = [
    {
      label: "Toplam Banner",
      value: stats.total,
      icon: Megaphone,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      label: "Aktif Banner",
      value: stats.active,
      icon: CheckCircle2,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Planlanmış",
      value: stats.scheduled,
      icon: Clock,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Süresi Dolmuş",
      value: stats.expired,
      icon: XCircle,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
    },
    {
      label: "Pasif Banner",
      value: stats.inactive,
      icon: Calendar,
      color: "text-gray-500",
      bgColor: "bg-gray-500/10",
    },
    {
      label: "Butonlu Banner",
      value: stats.withButton,
      icon: Eye,
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10",
    },
    {
      label: "Hedefli Banner",
      value: stats.targeted,
      icon: Target,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
    },
    {
      label: "Genel Banner",
      value: stats.global,
      icon: Building2,
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              {/* Arka plan dekoratif gradient */}
              <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>

              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                  </div>
                  <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                    <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                  </div>
                </div>

                {/* Alt bilgi çubuğu */}
                <div className="pt-3 border-t border-slate-100">
                  {item.label === "Aktif Banner" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-green-500 animate-pulse" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} aktif
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-green-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Planlanmış" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-blue-500" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          {item.value > 0 ? "Bekleyen banner" : "Planlanmış yok"}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-blue-600">{item.value}</span>
                    </div>
                  )}

                  {item.label === "Süresi Dolmuş" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-red-500" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} süresi dolmuş
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-red-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Pasif Banner" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-gray-500" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} pasif
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-gray-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Toplam Banner" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-purple-500"></span>
                        <span className="font-medium">Sistemde kayıtlı</span>
                      </div>
                      <span className="text-xs font-semibold text-purple-600">{item.value}</span>
                    </div>
                  )}

                  {item.label === "Butonlu Banner" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-indigo-500"></span>
                        <span className="font-medium">CTA içeren</span>
                      </div>
                      <span className="text-xs font-semibold text-indigo-600">{item.value}</span>
                    </div>
                  )}

                  {item.label === "Hedefli Banner" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-amber-500"></span>
                        <span className="font-medium">Firma bazlı hedefleme</span>
                      </div>
                      <span className="text-xs font-semibold text-amber-600">{item.value}</span>
                    </div>
                  )}

                  {item.label === "Genel Banner" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-cyan-500"></span>
                        <span className="font-medium">Tüm firmalara</span>
                      </div>
                      <span className="text-xs font-semibold text-cyan-600">{item.value}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
